import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetchV2 } from '../services/custom-fetch-v2';

interface PublicApiKeyInfo {
  available: boolean;
  enabled: boolean;
  keyPreview: string | null;
  key?: string;
  createdAt: string | null;
  lastUsedAt: string | null;
}

const PUBLIC_API_KEY_QUERY_KEY = ['usePublicApiKey'];

const getPublicApiKey = async (): Promise<PublicApiKeyInfo> => {
  const response = await customFetchV2<PublicApiKeyInfo>('/api/v2/users/me/public-api-key', {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Unable to get API key status');
  }

  return response.data;
};

const createPublicApiKey = async (): Promise<PublicApiKeyInfo> => {
  const response = await customFetchV2<PublicApiKeyInfo>('/api/v2/users/me/public-api-key', {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Unable to enable API access');
  }

  return response.data;
};

const regeneratePublicApiKey = async (): Promise<PublicApiKeyInfo> => {
  const response = await customFetchV2<PublicApiKeyInfo>('/api/v2/users/me/public-api-key/regenerate', {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Unable to regenerate API key');
  }

  return response.data;
};

const revokePublicApiKey = async (): Promise<PublicApiKeyInfo> => {
  const response = await customFetchV2<PublicApiKeyInfo>('/api/v2/users/me/public-api-key', {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Unable to disable API access');
  }

  return response.data;
};

const usePublicApiKey = () => useQuery(PUBLIC_API_KEY_QUERY_KEY, getPublicApiKey, {
  retry: false,
});

const useCreatePublicApiKeyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPublicApiKey,
    onSuccess: () => queryClient.invalidateQueries(PUBLIC_API_KEY_QUERY_KEY),
  });
};

const useRegeneratePublicApiKeyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: regeneratePublicApiKey,
    onSuccess: () => queryClient.invalidateQueries(PUBLIC_API_KEY_QUERY_KEY),
  });
};

const useRevokePublicApiKeyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: revokePublicApiKey,
    onSuccess: () => queryClient.invalidateQueries(PUBLIC_API_KEY_QUERY_KEY),
  });
};

export {
  PUBLIC_API_KEY_QUERY_KEY,
  usePublicApiKey,
  useCreatePublicApiKeyMutation,
  useRegeneratePublicApiKeyMutation,
  useRevokePublicApiKeyMutation,
};

export type { PublicApiKeyInfo };
